/**
 * Google Maps wrapper components for @vis.gl/react-google-maps
 * Provides Polygon, Circle, Polyline, DrawingManager, and TrafficLayer
 * as React components using the useMap() hook.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useMap as useGoogleMap } from '@vis.gl/react-google-maps';

// Shallow-compare two objects by their own enumerable keys
function shallowEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const k of keysA) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

// Compare two coordinate arrays by value (avoids setPath on identical data)
function pathEqual(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const pa = a[i], pb = b[i];
    if (!pa || !pb) return false;
    if (typeof pa.lat === 'function') {
      if (pa.lat() !== pb.lat() || pa.lng() !== pb.lng()) return false;
    } else {
      if (pa.lat !== pb.lat || pa.lng !== pb.lng) return false;
    }
  }
  return true;
}


// --- Google Map Controller ---
// Exposes map instance to parent via callback
export const GoogleMapController = ({ onMapReady }) => {
  const map = useGoogleMap();
  useEffect(() => {
    if (map && onMapReady) onMapReady(map);
  }, [map, onMapReady]);
  return null;
};

// --- Traffic Layer ---
export const GoogleTrafficLayer = ({ visible }) => {
  const map = useGoogleMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!map) return;
    if (!layerRef.current) {
      layerRef.current = new window.google.maps.TrafficLayer();
    }
    layerRef.current.setMap(visible ? map : null);
    return () => { if (layerRef.current) layerRef.current.setMap(null); };
  }, [map, visible]);

  return null;
};

// --- Polygon ---
export const GooglePolygon = ({ paths, options = {}, onClick, onLoad, onPathChanged }) => {
  const map = useGoogleMap();
  const shapeRef = useRef(null);
  const onClickRef = useRef(onClick);
  const onLoadRef = useRef(onLoad);
  const onPathChangedRef = useRef(onPathChanged);
  const prevPathsRef = useRef(null);
  const prevOptionsRef = useRef(null);
  const pathListenersRef = useRef([]);
  const shapeListenersRef = useRef([]);
  // Keep the latest callbacks in refs so the long-lived Google Maps listeners
  // always invoke the current props. Written in an effect (not during render) to
  // satisfy react-hooks/refs; the listeners only ever fire post-commit.
  useEffect(() => {
    onClickRef.current = onClick;
    onLoadRef.current = onLoad;
    onPathChangedRef.current = onPathChanged;
  });

  const clearListeners = useCallback((listeners) => {
    listeners.forEach((listener) => {
      try {
        window.google.maps.event.removeListener(listener);
      } catch {
        /* ignore */
      }
    });
  }, []);

  const emitPath = useCallback(() => {
    if (!shapeRef.current || !onPathChangedRef.current) return;
    const nextPaths = shapeRef.current.getPath().getArray().map((point) => ({ lat: point.lat(), lng: point.lng() }));
    onPathChangedRef.current(nextPaths);
  }, []);

  const bindPathListeners = useCallback(() => {
    if (!shapeRef.current) return;
    clearListeners(pathListenersRef.current);
    pathListenersRef.current = [];
    const path = shapeRef.current.getPath();
    ['insert_at', 'set_at', 'remove_at'].forEach((eventName) => {
      pathListenersRef.current.push(path.addListener(eventName, emitPath));
    });
  }, [clearListeners, emitPath]);

  useEffect(() => {
    if (!map) return;
    const polygon = new window.google.maps.Polygon({ map, paths: paths || [], ...options });
    shapeListenersRef.current = [
      polygon.addListener('click', () => onClickRef.current?.()),
      polygon.addListener('dragend', emitPath),
      polygon.addListener('mouseup', emitPath),
    ];
    shapeRef.current = polygon;
    prevPathsRef.current = paths;
    prevOptionsRef.current = options;
    bindPathListeners();
    onLoadRef.current?.(polygon);
    return () => {
      clearListeners(pathListenersRef.current);
      clearListeners(shapeListenersRef.current);
      pathListenersRef.current = [];
      shapeListenersRef.current = [];
      polygon.setMap(null);
      shapeRef.current = null;
    };
  }, [bindPathListeners, clearListeners, emitPath, map]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shapeRef.current) return;
    if (paths && paths !== prevPathsRef.current) {
      shapeRef.current.setPaths(paths);
      prevPathsRef.current = paths;
      bindPathListeners();
    }
    if (!shallowEqual(options, prevOptionsRef.current)) {
      shapeRef.current.setOptions(options);
      prevOptionsRef.current = options;
    }
  }, [bindPathListeners, options, paths]);

  return null;
};

// --- Circle ---
export const GoogleCircle = ({ center, radius, options = {}, onClick, onLoad, onChange }) => {
  const map = useGoogleMap();
  const shapeRef = useRef(null);
  const onClickRef = useRef(onClick);
  const onLoadRef = useRef(onLoad);
  const onChangeRef = useRef(onChange);
  const prevCenterRef = useRef(null);
  const prevRadiusRef = useRef(null);
  const prevOptionsRef = useRef(null);
  const listenersRef = useRef([]);
  // Keep the latest callbacks in refs so the long-lived Google Maps listeners
  // always invoke the current props. Written in an effect (not during render) to
  // satisfy react-hooks/refs; the listeners only ever fire post-commit.
  useEffect(() => {
    onClickRef.current = onClick;
    onLoadRef.current = onLoad;
    onChangeRef.current = onChange;
  });

  const emitChange = useCallback(() => {
    if (!shapeRef.current || !onChangeRef.current) return;
    const circleCenter = shapeRef.current.getCenter();
    onChangeRef.current({
      center: circleCenter ? { lat: circleCenter.lat(), lng: circleCenter.lng() } : null,
      radius: shapeRef.current.getRadius(),
    });
  }, []);

  useEffect(() => {
    if (!map) return;
    const circle = new window.google.maps.Circle({ map, center: center || {lat: 0, lng: 0}, radius: radius || 0, ...options });
    listenersRef.current = [
      circle.addListener('click', () => onClickRef.current?.()),
      circle.addListener('center_changed', emitChange),
      circle.addListener('radius_changed', emitChange),
      circle.addListener('dragend', emitChange),
      circle.addListener('mouseup', emitChange),
    ];
    shapeRef.current = circle;
    prevCenterRef.current = center;
    prevRadiusRef.current = radius;
    prevOptionsRef.current = options;
    onLoadRef.current?.(circle);
    return () => {
      listenersRef.current.forEach((listener) => {
        try {
          window.google.maps.event.removeListener(listener);
        } catch {
          /* ignore */
        }
      });
      listenersRef.current = [];
      circle.setMap(null);
      shapeRef.current = null;
    };
  }, [emitChange, map]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shapeRef.current) return;
    // Only setCenter when lat/lng actually changed
    if (center && (!prevCenterRef.current || center.lat !== prevCenterRef.current.lat || center.lng !== prevCenterRef.current.lng)) {
      shapeRef.current.setCenter(center);
      prevCenterRef.current = center;
    }
    if (radius != null && radius !== prevRadiusRef.current) {
      shapeRef.current.setRadius(radius);
      prevRadiusRef.current = radius;
    }
    if (!shallowEqual(options, prevOptionsRef.current)) {
      shapeRef.current.setOptions(options);
      prevOptionsRef.current = options;
    }
  }, [center, radius, options]);

  return null;
};

// --- Polyline ---
export const GooglePolyline = ({ path, options = {}, onClick, onLoad, onPathChanged }) => {
  const map = useGoogleMap();
  const shapeRef = useRef(null);
  const onClickRef = useRef(onClick);
  const onLoadRef = useRef(onLoad);
  const onPathChangedRef = useRef(onPathChanged);
  const prevPathRef = useRef(null);
  const prevOptionsRef = useRef(null);
  const pathListenersRef = useRef([]);
  const shapeListenersRef = useRef([]);
  // Keep the latest callbacks in refs so the long-lived Google Maps listeners
  // always invoke the current props. Written in an effect (not during render) to
  // satisfy react-hooks/refs; the listeners only ever fire post-commit.
  useEffect(() => {
    onClickRef.current = onClick;
    onLoadRef.current = onLoad;
    onPathChangedRef.current = onPathChanged;
  });

  const clearListeners = useCallback((listeners) => {
    listeners.forEach((listener) => {
      try {
        window.google.maps.event.removeListener(listener);
      } catch {
        /* ignore */
      }
    });
  }, []);

  const emitPath = useCallback(() => {
    if (!shapeRef.current || !onPathChangedRef.current) return;
    const nextPath = shapeRef.current.getPath().getArray().map((point) => ({ lat: point.lat(), lng: point.lng() }));
    onPathChangedRef.current(nextPath);
  }, []);

  const bindPathListeners = useCallback(() => {
    if (!shapeRef.current) return;
    clearListeners(pathListenersRef.current);
    pathListenersRef.current = [];
    const currentPath = shapeRef.current.getPath();
    ['insert_at', 'set_at', 'remove_at'].forEach((eventName) => {
      pathListenersRef.current.push(currentPath.addListener(eventName, emitPath));
    });
  }, [clearListeners, emitPath]);

  useEffect(() => {
    if (!map) return;
    // Apply the current path/options at creation so a re-created polyline (StrictMode
    // double-invoke or map instance change) is never left with an empty path.
    const polyline = new window.google.maps.Polyline({ map, path: path || [], ...options });
    shapeListenersRef.current = [
      polyline.addListener('click', (e) => onClickRef.current?.(e)),
      polyline.addListener('dragend', emitPath),
      polyline.addListener('mouseup', emitPath),
    ];
    shapeRef.current = polyline;
    prevPathRef.current = path;
    prevOptionsRef.current = options;
    bindPathListeners();
    onLoadRef.current?.(polyline);
    return () => {
      clearListeners(pathListenersRef.current);
      clearListeners(shapeListenersRef.current);
      pathListenersRef.current = [];
      shapeListenersRef.current = [];
      polyline.setMap(null);
      shapeRef.current = null;
    };
  }, [bindPathListeners, clearListeners, emitPath, map]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shapeRef.current) return;
    // Only setPath when coordinates actually changed
    if (path && !pathEqual(path, prevPathRef.current)) {
      shapeRef.current.setPath(path);
      prevPathRef.current = path;
      bindPathListeners();
    }
    if (!shallowEqual(options, prevOptionsRef.current)) {
      shapeRef.current.setOptions(options);
      prevOptionsRef.current = options;
    }
  }, [bindPathListeners, options, path]);

  return null;
};

// --- Drawing Manager ---
// --- Terra Draw Manager (replaces deprecated google.maps.drawing.DrawingManager) ---
export const TerraDrawManager = ({
  drawingMode,
  zoneColor,
  routeColor,
  onPolygonComplete,
  onCircleComplete,
  onRectangleComplete,
  onPolylineComplete,
}) => {
  const map = useGoogleMap();
  const drawRef = useRef(null);
  const modeRef = useRef(null);
  const callbacksRef = useRef({ onPolygonComplete, onCircleComplete, onRectangleComplete, onPolylineComplete });
  // Keep the latest draw-complete callbacks in a ref (read inside async TerraDraw
  // event handlers). Written in an effect, not during render, to satisfy react-hooks/refs.
  useEffect(() => {
    callbacksRef.current = { onPolygonComplete, onCircleComplete, onRectangleComplete, onPolylineComplete };
  });

  useEffect(() => {
    if (!map || !window.google?.maps) return;

    let draw = null;
    let destroyed = false;

    const init = async () => {
      const { TerraDraw, TerraDrawPolygonMode, TerraDrawCircleMode, TerraDrawRectangleMode, TerraDrawLineStringMode } = await import('terra-draw');
      const { TerraDrawGoogleMapsAdapter } = await import('terra-draw-google-maps-adapter');

      if (destroyed) return;

      const adapter = new TerraDrawGoogleMapsAdapter({ map, lib: window.google.maps, coordinatePrecision: 9 });

      draw = new TerraDraw({
        adapter,
        modes: [
          new TerraDrawPolygonMode({ allowSelfIntersections: false }),
          new TerraDrawCircleMode(),
          new TerraDrawRectangleMode(),
          new TerraDrawLineStringMode(),
        ],
      });

      const startDraw = () => {
        if (destroyed || drawRef.current) return;
        try {
          draw.start();
          drawRef.current = draw;

          draw.on('finish', (id) => {
        const snapshot = draw.getSnapshot();
        const feature = snapshot.find((f) => f.id === id);
        if (!feature) return;

        const geom = feature.geometry;
        const mode = feature.properties?.mode;

        if (mode === 'polygon' && callbacksRef.current.onPolygonComplete) {
          const ring = geom.coordinates[0];
          const coords = ring.map((c) => [c[1], c[0]]);
          if (coords.length > 0 && coords[0][0] === coords[coords.length - 1][0] && coords[0][1] === coords[coords.length - 1][1]) coords.pop();
          callbacksRef.current.onPolygonComplete(coords);
        } else if (mode === 'circle' && callbacksRef.current.onCircleComplete) {
          const ring = geom.coordinates[0];
          let sumLat = 0; let sumLng = 0;
          for (const c of ring) { sumLng += c[0]; sumLat += c[1]; }
          const centerLng = sumLng / ring.length;
          const centerLat = sumLat / ring.length;
          const firstLng = ring[0][0]; const firstLat = ring[0][1];
          const R = 6371000;
          const dLat = (firstLat - centerLat) * Math.PI / 180;
          const dLng = (firstLng - centerLng) * Math.PI / 180;
          const a = Math.sin(dLat / 2) ** 2 + Math.cos(centerLat * Math.PI / 180) * Math.cos(firstLat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
          const radius = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          callbacksRef.current.onCircleComplete([centerLat, centerLng], radius);
        } else if (mode === 'rectangle' && callbacksRef.current.onRectangleComplete) {
          const ring = geom.coordinates[0];
          const coords = ring.map((c) => [c[1], c[0]]);
          if (coords.length > 0 && coords[0][0] === coords[coords.length - 1][0] && coords[0][1] === coords[coords.length - 1][1]) coords.pop();
          callbacksRef.current.onRectangleComplete(coords);
        } else if (mode === 'linestring' && callbacksRef.current.onPolylineComplete) {
          const coords = geom.coordinates.map((c) => [c[1], c[0]]);
          callbacksRef.current.onPolylineComplete(coords);
        }

        try { draw.removeFeatures([id]); } catch { /* already removed */ }
          });

          if (drawingMode) {
            draw.setMode(drawingMode);
            modeRef.current = drawingMode;
          }
        } catch {
          // OverlayView container not ready yet — retry after delay
          if (!destroyed) setTimeout(startDraw, 200);
        }
      };

      // Wait for map projection to be ready before starting Terra Draw
      if (map.getProjection()) {
        startDraw();
      } else {
        window.google.maps.event.addListenerOnce(map, 'projection_changed', startDraw);
      }
    };

    init();

    return () => {
      destroyed = true;
      if (drawRef.current) {
        try { drawRef.current.stop(); } catch { /* ignore */ }
        drawRef.current = null;
      } else if (draw) {
        try { draw.stop(); } catch { /* never started or already stopped */ }
      }
    };
  // The TerraDraw instance is created once per map. drawingMode is applied by the
  // separate effect just below; re-initialising here on every mode change would
  // destroy & recreate the whole draw instance. Intentional [map]-only dep.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (!drawRef.current) return;
    if (drawingMode && drawingMode !== modeRef.current) {
      try {
        drawRef.current.setMode(drawingMode);
        modeRef.current = drawingMode;
      } catch { /* mode not ready yet */ }
    }
  }, [drawingMode]);

  return null;
};

// --- Idle listener (for syncing map state) ---
export const GoogleIdleListener = ({ onIdle }) => {
  const map = useGoogleMap();
  const onIdleRef = useRef(onIdle);
  useEffect(() => { onIdleRef.current = onIdle; });

  useEffect(() => {
    if (!map) return;
    const listener = map.addListener('idle', () => onIdleRef.current?.());
    return () => window.google.maps.event.removeListener(listener);
  }, [map]);

  return null;
};

// --- Weather Tile Overlay (OpenWeatherMap) ---
export const GoogleWeatherOverlay = ({ visible, layer = 'precipitation_new', apiKey }) => {
  const map = useGoogleMap();
  const overlayRef = useRef(null);
  const currentLayerRef = useRef(null);
  const mapRef = useRef(null);

  // Store map ref once
  useEffect(() => { if (map) mapRef.current = map; }, [map]);

  // Add/remove overlay only when visibility or layer changes
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !apiKey) return;

    // Remove existing overlay
    if (overlayRef.current) {
      try {
        const types = m.overlayMapTypes;
        for (let i = types.getLength() - 1; i >= 0; i--) {
          if (types.getAt(i) === overlayRef.current) types.removeAt(i);
        }
      } catch (e) { /* ignore */ }
      overlayRef.current = null;
      currentLayerRef.current = null;
    }

    if (!visible) return;

    // Cache-bust bucket: changes every 10 minutes so browser caches tiles within that window
    const cacheBucket = Math.floor(Date.now() / 600000);

    const tileLayer = new window.google.maps.ImageMapType({
      getTileUrl: (coord, zoom) =>
        `https://tile.openweathermap.org/map/${layer}/${zoom}/${coord.x}/${coord.y}.png?appid=${apiKey}&_t=${cacheBucket}`,
      tileSize: new window.google.maps.Size(256, 256),
      opacity: 0.85,
      name: 'weather',
    });
    m.overlayMapTypes.push(tileLayer);
    overlayRef.current = tileLayer;
    currentLayerRef.current = layer;

  }, [visible, layer, apiKey]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const m = mapRef.current;
      if (overlayRef.current && m?.overlayMapTypes) {
        try {
          const types = m.overlayMapTypes;
          for (let i = types.getLength() - 1; i >= 0; i--) {
            if (types.getAt(i) === overlayRef.current) types.removeAt(i);
          }
        } catch (e) { /* ignore */ }
        overlayRef.current = null;
      }
    };
  }, []);

  return null;
};


export const DeckGLOverlay = ({ layers }) => {
  const map = useGoogleMap();
  const overlayRef = useRef(null);
  const mapStored = useRef(null);
  const layersRef = useRef(layers);
  // Tracks whether this overlay instance has unmounted. Guards the async dynamic
  // import() below from creating a deck.gl overlay (and its rAF AnimationLoop) AFTER
  // the component already unmounted — which would attach a Deck instance to a dead
  // map with no cleanup path, leaking a render loop permanently.
  const cancelledRef = useRef(false);

  // Store map ref once — never tear down overlay on re-render
  useEffect(() => {
    if (map && !mapStored.current) {
      mapStored.current = map;
      import('@deck.gl/google-maps').then(({ GoogleMapsOverlay }) => {
        if (cancelledRef.current) return; // unmounted before the async import resolved
        if (!overlayRef.current) {
          overlayRef.current = new GoogleMapsOverlay({
            interleaved: false,
            _typedArrayManagerProps: { overAlloc: 1, poolSize: 0 },
          });
          overlayRef.current.setMap(map);
          if (layersRef.current) {
            overlayRef.current.setProps({ layers: layersRef.current });
          }
          // Suppress Canvas2D readback warning by patching deck's internal canvas
          try {
            const canvases = map.getDiv()?.querySelectorAll('canvas');
            canvases?.forEach(c => {
              const origGetContext = c.getContext.bind(c);
              c.getContext = (type, attrs) => origGetContext(type, { willReadFrequently: true, ...attrs });
            });
          } catch { /* non-critical */ }
        }
      });
    }
  }, [map]);

  // Update layers without tearing down the overlay
  useEffect(() => {
    layersRef.current = layers;
    if (overlayRef.current) {
      overlayRef.current.setProps({ layers: layers || [] });
    }
  }, [layers]);

  // Cleanup only on unmount — MUST finalize() the deck.gl overlay, not just setMap(null).
  // GoogleMapsOverlay.setMap(null) only detaches the OverlayView; it leaves the underlying
  // Deck instance (and its luma.gl AnimationLoop, i.e. a self-rescheduling
  // requestAnimationFrame loop) running forever. Each Live Tracking visit would then leak
  // one more render loop, and N overlapping loops doing N× work is the root cause of the
  // "[Violation] 'message' handler took ~190ms" spike that worsens after navigating away
  // and back. finalize() internally calls setMap(null) AND destroyDeckInstance(this._deck),
  // which stops the AnimationLoop and releases GPU resources.
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      const ov = overlayRef.current;
      overlayRef.current = null;
      if (ov) {
        try {
          if (typeof ov.finalize === 'function') ov.finalize();
          else ov.setMap(null);
        } catch { /* non-critical */ }
      }
    };
  }, []);

  return null;
};

